import Modal from "./Modal.jsx";
import { Icon } from "./components/Media.jsx";
import {
  aggregateApiHealth,
  AUTO_SOURCE_ID,
  providerAvailability,
  providerFromHealth,
  sourceChoiceForSelection,
} from "./settingsModel.js";

function checkedTime(value) {
  const date = new Date(value || "");
  return Number.isNaN(date.getTime())
    ? "尚未完成"
    : new Intl.DateTimeFormat("zh-TW", {
        hour: "2-digit",
        minute: "2-digit",
        second: "2-digit",
      }).format(date);
}

export default function SettingsSheet({
  health,
  install,
  onClose,
  onInstall,
  onSelectSource,
  selectedSources,
  sourceOptions,
}) {
  const sourceIds = sourceOptions.map((source) => source.id);
  const sourceChoice = sourceChoiceForSelection(selectedSources, sourceIds);
  const apiHealth = aggregateApiHealth(health.data, sourceIds);

  return (
    <Modal className="settings-modal" label="Music Life 設定" onClose={onClose}>
      <section className="settings-sheet">
        <div className="destination-head settings-head">
          <div><span>Music Life</span><h2>設定</h2></div>
          <button type="button" onClick={onClose} aria-label="關閉設定"><Icon name="close" size={19}/></button>
        </div>

        <section className="settings-section" aria-labelledby="api-health-title">
          <div className="settings-section__head">
            <div>
              <h3 id="api-health-title">API 狀態監控</h3>
            </div>
            <button type="button" className="text-button" onClick={() => void health.refresh()}
              disabled={health.phase === "loading" || health.phase === "refreshing"}>
              {health.phase === "loading" || health.phase === "refreshing"
                ? <><span className="spinner"/>檢查中</>
                : <><Icon name="refresh" size={17}/>重新檢查</>}
            </button>
          </div>
          {health.error && <p className="settings-warning" role="status">{health.error}；播放仍沿用目前設定。</p>}
          <p className="settings-meta">最後檢查：{checkedTime(health.data?.checkedAt)}</p>

          <div className="api-source-list" aria-label="切換 API 來源">
            <button type="button" className={sourceChoice === AUTO_SOURCE_ID ? "active" : ""}
              aria-pressed={sourceChoice === AUTO_SOURCE_ID} onClick={() => onSelectSource(AUTO_SOURCE_ID)}>
              <span className="source-radio" aria-hidden="true">{sourceChoice === AUTO_SOURCE_ID ? "●" : "○"}</span>
              <span><strong>自動／多來源</strong><small>同時使用可選鏡像，由播放引擎自動備援</small></span>
              <span className="source-state"><span className="health-dot" data-tone={apiHealth.tone}/>{apiHealth.label}</span>
            </button>
            {sourceOptions.map((source) => {
              const provider = providerFromHealth(health.data, source.id);
              const availability = providerAvailability(provider);
              const unavailable = health.phase === "ready" && !availability.usable;
              const latencyMs = provider?.probeLatencyMs ?? provider?.latencyMs;
              return (
                <button key={source.id} type="button" className={sourceChoice === source.id ? "active" : ""}
                  aria-pressed={sourceChoice === source.id} disabled={unavailable}
                  onClick={() => onSelectSource(source.id)}>
                  <span className="source-radio" aria-hidden="true">{sourceChoice === source.id ? "●" : "○"}</span>
                  <span><strong>{source.label} API</strong><small>{source.detail || "指定單一來源；失效時可回到自動模式"}</small></span>
                  <span className="source-state"><span className="health-dot" data-tone={availability.tone}/>
                    {availability.label}{Number.isFinite(latencyMs) ? ` · ${Math.round(latencyMs)} ms` : ""}</span>
                </button>
              );
            })}
          </div>
          <p className="settings-note">來源選擇套用於後續搜尋與播放資料刷新；最終播放來源仍由 Resolver（解析器）驗證。</p>
        </section>

        <section className="settings-section" aria-labelledby="pwa-title">
          <div className="settings-section__head">
            <div><h3 id="pwa-title">安裝應用程式</h3><p>加入手機桌面或電腦，使用獨立視窗開啟。</p></div>
          </div>
          <div className="install-card">
            <span className="install-icon"><Icon name="download" size={23}/></span>
            <span>
              <strong>{install.status === "installed" ? "Music Life 已安裝" : "安裝 Music Life"}</strong>
              <small>{install.status === "installed"
                ? "目前正以 PWA 應用程式模式執行"
                : install.status === "available"
                  ? "安裝後可從桌面直接開啟"
                  : install.platform === "ios"
                    ? "請使用瀏覽器分享選單，再點選「加入主畫面」"
                    : "請從瀏覽器選單選擇「安裝應用程式」或「加入主畫面」"}</small>
            </span>
            {install.status === "available" && <button type="button" className="primary-button" onClick={onInstall}>安裝</button>}
            {install.status === "installed" && <span className="installed-mark" aria-label="已安裝">✓</span>}
          </div>
          <p className="settings-note">PWA 僅快取應用程式介面；搜尋與音樂播放仍需網路連線。</p>
        </section>
      </section>
    </Modal>
  );
}
